import React from 'react'
import { Row, Col } from 'reactstrap'
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import { Chip, MenuItem, Select } from '@mui/material'
import BugReportIcon from '@mui/icons-material/BugReport'
import EventIcon from '@mui/icons-material/Event'
import { withFirebase } from '../../components/Fb'
import {
  INQUIRY_STATUSES,
  buildStatusUpdate,
  detailRows,
  availabilityLabel,
  normaliseInquiry,
  occurrenceRows,
  statusColor,
  statusHistory,
  statusLabel,
  toDisplayDate,
} from '../../components/utils/inquiries'

const eurFormatter = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })

// The readable summary at the top of Tiedot: who asked, how to reach them, what they said.
// Everything else about the enquiry is either in the grid, in the Vuorot dialog, or behind
// the collapsed raw field list.
const SUMMARY_FIELDS = [
  { key: 'name', label: 'Nimi' },
  { key: 'email', label: 'Sähköposti' },
  { key: 'phone', label: 'Puhelin' },
  { key: 'message', label: 'Viesti' },
]

// The raw-JSON pane is a developer affordance, so it appears only when the UI is being
// served from a local machine. Keyed on the hostname rather than NODE_ENV: a production
// build served from localhost (npm run preview, serve -s build) is still someone
// debugging, while a development build would never be served from the preview host.
const IS_LOCALHOST =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]', '::1'].includes(window.location.hostname)

function ExportToolbar() {
  // ';' + utf8WithBom is the house convention - anything else opens as one column in a
  // Finnish Excel.
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport csvOptions={{ fileName: 'kyselyt', delimiter: ';', utf8WithBom: true }} />
    </GridToolbarContainer>
  )
}

/**
 * Kyselyt - the enquiries a Tiedustelu recurring-sale campaign collects.
 *
 * These documents are written by the backend, so this view reads them defensively (see
 * utils/inquiries.js) and only ever changes `status`, recording who moved it and when. The
 * details dialog shows every field the document actually holds, so a wrong guess about a
 * field name hides nothing.
 */
class RecurringInquiriesContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      inquiries: [],
      statusFilter: 'new',
      selected: null,
      showFields: false,
      showJson: false,
      jsonCopied: false,
      showOccurrences: false,
      savingId: null,
      error: null,
    }
  }

  componentDidMount() {
    this.updateData()
  }

  updateData = async () => {
    try {
      const docs = await this.props.firebase.getRecurringInquiries()
      const inquiries = docs
        .map((doc) => normaliseInquiry(doc))
        .sort((a, b) => b.createdsort - a.createdsort)
      this.setState({ inquiries, loading: false, error: null })
    } catch (error) {
      console.error('Inquiries failed to load', error)
      this.setState({
        inquiries: [],
        loading: false,
        error: 'Kyselyiden lataus epäonnistui: ' + ((error && error.message) || 'tuntematon virhe'),
      })
    }
  }

  changeStatus = async (row, status) => {
    if (!row || status === row.status) return
    this.setState({ savingId: row.id, error: null })
    const user = this.props.firebase.auth.currentUser
    // Client timestamp for the log entry, because Firestore forbids a serverTimestamp()
    // sentinel inside arrayUnion. The top-level `updated` still gets the server clock.
    const patch = buildStatusUpdate(status, user, this.props.firebase.getToday())
    const result = await this.props.firebase.updateRecurringInquiryStatus(row.id, patch)
    if (result && result.error) {
      this.setState({ savingId: null, error: 'Tilan päivitys epäonnistui: ' + result.error })
      return
    }
    this.setState({ savingId: null, loading: true }, this.updateData)
  }

  getFiltered = () => {
    const { inquiries, statusFilter } = this.state
    if (statusFilter === 'all') return inquiries
    return inquiries.filter((i) => i.status === statusFilter)
  }

  countFor = (value) =>
    value === 'all' ? this.state.inquiries.length : this.state.inquiries.filter((i) => i.status === value).length

  getColDefinitions = () => [
    {
      field: 'created',
      headerName: 'Saapunut',
      width: 140,
      // The value is an ISO-ish string: it sorts chronologically as text whatever shape
      // the stored timestamp had, and exports to CSV readably. The cell shows the Finnish
      // format. Returning the numeric sort key here would export raw milliseconds.
      valueGetter: (value, row) => toDisplayDate(row.created, 'YYYY-MM-DD HH:mm'),
      renderCell: (params) => toDisplayDate(params.row.created) || '—',
    },
    { field: 'name', headerName: 'Nimi', flex: 1.5, minWidth: 140 },
    { field: 'email', headerName: 'Sähköposti', flex: 2, minWidth: 170 },
    { field: 'campaign', headerName: 'Kampanja', flex: 1.5, minWidth: 130 },
    {
      field: 'slotsummary',
      headerName: 'Vuoro',
      flex: 1.8,
      minWidth: 170,
      // Sorted by position in the week, not alphabetically - 'Ke' must not come before 'Ma'.
      sortComparator: (a, b, pa, pb) => (pa.api.getRow(pa.id).slotsort || 0) - (pb.api.getRow(pb.id).slotsort || 0),
      renderCell: (params) =>
        params.row.slotsummary || <span style={{ color: '#adb5bd' }}>—</span>,
    },
    { field: 'resource', headerName: 'Kenttä', flex: 1.2, minWidth: 110 },
    {
      field: 'occurrences',
      headerName: 'Kertaa',
      width: 90,
      type: 'number',
    },
    {
      field: 'price',
      // Labelled per occurrence, because that is what it is: the same 35-week season is
      // 68 for a 2 h slot and 85 for a 2,5 h slot.
      headerName: 'Hinta/kerta',
      width: 120,
      type: 'number',
      valueFormatter: (value) => (value === '' || value === null || value === undefined ? '' : eurFormatter.format(Number(value) || 0)),
    },
    {
      field: 'fullprice',
      headerName: 'Yhteensä',
      width: 120,
      type: 'number',
      valueFormatter: (value) => (value === '' || value === null || value === undefined ? '' : eurFormatter.format(Number(value) || 0)),
    },
    { field: 'message', headerName: 'Viesti', flex: 2, minWidth: 160 },
    {
      field: 'status',
      headerName: 'Tila',
      width: 150,
      renderCell: (params) => (
        <Select
          size='small'
          variant='standard'
          value={params.row.status}
          disabled={this.state.savingId === params.row.id}
          onChange={(e) => this.changeStatus(params.row, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          sx={{ fontSize: 13 }}
        >
          {INQUIRY_STATUSES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'changedby',
      headerName: 'Tilan muutti',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) =>
        params.row.changedby ? (
          <span style={{ fontSize: 12 }}>
            {params.row.changedby}
            <br />
            <span style={{ color: '#6c757d' }}>{toDisplayDate(params.row.changedat)}</span>
          </span>
        ) : (
          <span style={{ color: '#adb5bd' }}>—</span>
        ),
    },
    {
      field: 'details',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => (
        <Button size='small' onClick={() => this.openDetails(params.row)}>
          Tiedot
        </Button>
      ),
    },
  ]

  renderStatusFilters = () => {
    const options = [...INQUIRY_STATUSES, { label: 'Kaikki', value: 'all' }]
    return options.map((option) => (
      <Chip
        key={option.value}
        label={option.label + ' (' + this.countFor(option.value) + ')'}
        clickable
        size='small'
        color='primary'
        variant={this.state.statusFilter === option.value ? 'filled' : 'outlined'}
        onClick={() => this.setState({ statusFilter: option.value })}
        style={{ marginRight: 4, marginBottom: 4 }}
      />
    ))
  }

  // showOccurrences has to be reset with the rest, or closing Tiedot and opening another
  // inquiry pops the occurrence dialog straight back open over it.
  openDetails = (row) =>
    this.setState({ selected: row, showFields: false, showJson: false, jsonCopied: false, showOccurrences: false })

  closeDetails = () =>
    this.setState({ selected: null, showFields: false, showJson: false, jsonCopied: false, showOccurrences: false })

  // These documents are written by the backend and their shape is not ours to assume, so
  // the raw JSON is one click away rather than something you have to open the Firebase
  // console for. Local only - see IS_LOCALHOST.
  copyJson = async () => {
    const raw = this.state.selected && this.state.selected.raw
    if (!raw) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(raw, null, 2))
      this.setState({ jsonCopied: true })
    } catch (error) {
      // Clipboard needs a secure context and permission; the JSON is on screen either way.
      console.error('Copying inquiry JSON failed', error)
    }
  }

  renderDetails = () => {
    const row = this.state.selected
    if (!row) return null
    const history = statusHistory(row.raw)
    const rows = detailRows(row.raw)
    const occurrences = occurrenceRows(row.slot)

    return (
      <Dialog open={!!row} onClose={this.closeDetails} maxWidth='sm' fullWidth>
        <DialogTitle>{row.name || row.email || 'Kysely'}</DialogTitle>
        <DialogContent>
          <Chip size='small' label={statusLabel(row.status)} color={statusColor(row.status)} sx={{ mb: 2 }} />

          {/* What the admin actually came here for, under Finnish labels. The raw field
              list below is keyed by the backend's own field names (campaignid, doctype,
              creatorId), which mean nothing to whoever is answering the enquiry - so it
              starts collapsed. */}
          {SUMMARY_FIELDS.map((field) => (
            <Row key={field.key} className='mb-1'>
              <Col xs='4'>
                <Typography variant='body2' color='text.secondary'>
                  {field.label}
                </Typography>
              </Col>
              <Col xs='8'>
                <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                  {row[field.key] || '—'}
                </Typography>
              </Col>
            </Row>
          ))}

          <Row className='mt-2'>
            <Col>
              <Button
                size='small'
                onClick={() => this.setState({ showFields: !this.state.showFields })}
              >
                {this.state.showFields
                  ? 'Piilota kaikki kentät'
                  : 'Näytä kaikki kentät (' + rows.length + ')'}
              </Button>
            </Col>
          </Row>

          {/* Every field the document actually carries, so nothing is hidden by this view
              guessing a field name wrong. */}
          {this.state.showFields &&
            rows.map((field) => (
              <Row key={field.key} className='mb-1'>
                <Col xs='5'>
                  <Typography variant='body2' color='text.secondary'>
                    {field.key}
                  </Typography>
                </Col>
                <Col xs='7'>
                  <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                    {field.display || '—'}
                  </Typography>
                </Col>
              </Row>
            ))}

          <Divider sx={{ mt: 3, mb: 2 }} />
          {/* The occurrences get their own dialog. A season is ~35 rows, and inlining them
              here on top of the fields, the history and the JSON made this dialog far too
              tall to scan. */}
          <Typography variant='subtitle2'>Vuorot</Typography>
          {occurrences.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Kyselyssä ei ole yksittäisiä vuoroja.
            </Typography>
          ) : (
            <Row className='align-items-center'>
              <Col xs='auto'>
                <Typography variant='body2' color='text.secondary'>
                  {occurrences.length} kertaa, {row.seasonfirst}&ndash;{row.seasonlast}
                  {' · Kyselyhetken tilanne: '}
                  {row.unavailable > 0 ? (
                    <span style={{ color: '#dc3545' }}>{availabilityLabel(row.unavailable)}</span>
                  ) : (
                    availabilityLabel(row.unavailable)
                  )}
                </Typography>
              </Col>
              <Col xs='auto'>
                <Button
                  size='small'
                  startIcon={<EventIcon />}
                  onClick={() => this.setState({ showOccurrences: true })}
                >
                  Näytä vuorot
                </Button>
              </Col>
            </Row>
          )}

          <Divider sx={{ mt: 3, mb: 2 }} />
          <Typography variant='subtitle2'>Tilan historia</Typography>
          {history.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Tilaa ei ole vielä muutettu.
            </Typography>
          ) : (
            history.map((entry, index) => (
              <Row key={index} className='mt-1'>
                <Col xs='4'>
                  <Typography variant='body2'>{statusLabel(entry.status)}</Typography>
                </Col>
                <Col xs='4'>
                  <Typography variant='body2' color='text.secondary'>
                    {toDisplayDate(entry.at) || '—'}
                  </Typography>
                </Col>
                <Col xs='4'>
                  <Typography variant='body2' color='text.secondary' sx={{ wordBreak: 'break-word' }}>
                    {entry.by || '—'}
                  </Typography>
                </Col>
              </Row>
            ))
          )}

          {IS_LOCALHOST && <Divider sx={{ mt: 3, mb: 1 }} />}
          {IS_LOCALHOST && (
            <Row>
              <Col>
                <Button
                  size='small'
                  startIcon={<BugReportIcon />}
                  onClick={() => this.setState({ showJson: !this.state.showJson, jsonCopied: false })}
                >
                  {this.state.showJson ? 'Piilota JSON' : 'Näytä JSON'}
                </Button>
                {this.state.showJson && (
                  <Button size='small' onClick={this.copyJson}>
                    {this.state.jsonCopied ? 'Kopioitu' : 'Kopioi'}
                  </Button>
                )}
              </Col>
            </Row>
          )}
          {IS_LOCALHOST && this.state.showJson && (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 12,
                backgroundColor: '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                padding: 8,
                marginTop: 8,
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              {JSON.stringify(row.raw, null, 2)}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeDetails}>Sulje</Button>
        </DialogActions>
      </Dialog>
    )
  }

  // Its own dialog, stacked over Tiedot. Wider and taller than a section inside Tiedot
  // could be, which is the point - 35 rows want scanning, not squinting.
  renderOccurrences = () => {
    const row = this.state.selected
    if (!row) return null
    const occurrences = occurrenceRows(row.slot)

    return (
      <Dialog
        open={this.state.showOccurrences}
        onClose={() => this.setState({ showOccurrences: false })}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          Vuorot &mdash; {row.name || row.email || 'Kysely'}
          <Typography variant='body2' color='text.secondary'>
            {occurrences.length} kertaa, {row.seasonfirst}&ndash;{row.seasonlast}
            {row.resource ? ' · ' + row.resource : ''}
            {' · Kyselyhetken tilanne: ' + availabilityLabel(row.unavailable)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {occurrences.map((occurrence) => (
            <Row
              key={occurrence.key}
              className='py-1'
              style={{
                backgroundColor: occurrence.available ? 'transparent' : '#fdecea',
                borderBottom: '1px solid #f1f3f5',
                margin: 0,
              }}
            >
              <Col xs='2'>
                <Typography variant='body2' color='text.secondary'>
                  {occurrence.key + 1}.
                </Typography>
              </Col>
              <Col xs='4'>
                <Typography variant='body2'>{occurrence.date}</Typography>
              </Col>
              <Col xs='3'>
                <Typography variant='body2' color='text.secondary'>
                  {occurrence.time}
                </Typography>
              </Col>
              <Col xs='3'>
                <Typography
                  variant='body2'
                  sx={{ color: occurrence.available ? 'text.secondary' : '#dc3545' }}
                >
                  {occurrence.available ? 'Vapaa' : 'Ei vapaana'}
                </Typography>
              </Col>
            </Row>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ showOccurrences: false })}>Sulje</Button>
        </DialogActions>
      </Dialog>
    )
  }

  render() {
    if (this.state.loading) {
      return <div className='content'>Loading ...</div>
    }

    return (
      <div className='content'>
        {this.state.error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => this.setState({ error: null })}>
            {this.state.error}
          </Alert>
        )}
        {this.state.inquiries.length === 0 && !this.state.error && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Ei kyselyitä. Kyselyt syntyvät myyntikampanjoista, joiden maksutapa on Tiedustelu.
          </Alert>
        )}
        <Row>
          <Col className='mb-2'>{this.renderStatusFilters()}</Col>
        </Row>
        <Row>
          <Col xs='12'>
            <div style={{ display: 'flex', minHeight: 'calc(100vh - 260px)', backgroundColor: '#FFFFFF' }}>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <DataGrid
                  rows={this.getFiltered()}
                  columns={this.getColDefinitions()}
                  density='compact'
                  showToolbar
                  slots={{ toolbar: ExportToolbar }}
                  getRowHeight={() => 'auto'}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  pageSizeOptions={[25, 50, 100]}
                  sx={{ '& .MuiDataGrid-cell': { py: 0.5 } }}
                />
              </div>
            </div>
          </Col>
        </Row>
        {this.renderDetails()}
        {this.renderOccurrences()}
      </div>
    )
  }
}

export default withFirebase(RecurringInquiriesContainer)
